import React, { useState } from 'react';
import { Badge, Tabs } from 'antd';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';

import type { MentionUser } from '../../../components/editor/RichTextEditor';
import type { TaskStore } from '../../../stores/kanban/TaskStore';
import type { KanbanTask } from '../../../stores/KanbanTaskStores';
import InlineEditableField from '../InlineEditableField';
import TaskAttachments from './TaskAttachments';
import TaskChanges from './TaskChanges';
import TaskComments from './TaskComments';
import TaskDescription from './TaskDescription';
import TaskTimeTab from './TaskTimeTab';
import { useTaskField } from './useTaskField';

export type TaskMainProps = {
  task: KanbanTask;
  store: TaskStore;
  readOnly: boolean;
  /** Anonymous (public share-link) board; threaded down to `TaskAttachments` to hide attachment delete. */
  isAnonymous: boolean;
  mentionUsers: MentionUser[];
  currentUserId?: string;
  projectName?: string;
};

/**
 * Left column of the drawer: title, description, attachments and the Comments/Changes/Time tabs.
 */
const TaskMain: React.FC<TaskMainProps> = observer(
  ({ task, store, readOnly, isAnonymous, mentionUsers, currentUserId, projectName }) => {
    const { t } = useTranslation();
    const [commentCount, setCommentCount] = useState(0);

    const title = useTaskField<string>({
      identity: task.id,
      value: task.title,
      save: (next) => store.updateTaskTitle(task.id, next),
      successMessage: t('TaskTitleUpdated'),
      errorMessage: t('FailedToUpdateTaskTitle'),
    });

    const tabs = [
      {
        key: 'comments',
        label: (
          <span className="task-drawer__tab-label">
            {t('Comments')}
            <Badge count={commentCount} className="task-drawer__tab-badge" />
          </span>
        ),
        children: (
          <TaskComments
            task={task}
            store={store}
            readOnly={readOnly}
            mentionUsers={mentionUsers}
            currentUserId={currentUserId}
            onCountChange={setCommentCount}
          />
        ),
      },
      {
        key: 'changes',
        label: t('Changes'),
        children: <TaskChanges store={store} />,
      },
      {
        key: 'time',
        label: t('Time'),
        children: <TaskTimeTab task={task} store={store} readOnly={readOnly} projectName={projectName} />,
      },
    ];

    return (
      <div className="task-drawer__main">
        <div className="task-drawer__title">
          <InlineEditableField
            value={task.title}
            onSave={async (next) => {
              // `InlineEditableField` owns the edit state and only closes when `onSave` resolves, so
              // a rejected save is what keeps the typed title on screen for a retry.
              if (!(await title.commit(next))) {
                throw new Error('Task title was not saved');
              }
            }}
            disabled={readOnly}
            viewComponent={<span className="task-drawer__title-text">{task.title}</span>}
          />
        </div>

        <TaskDescription task={task} store={store} readOnly={readOnly} mentionUsers={mentionUsers} />
        <TaskAttachments task={task} store={store} readOnly={readOnly} isAnonymous={isAnonymous} />
        <Tabs items={tabs} className="task-drawer__tabs" />
      </div>
    );
  },
);

export default TaskMain;
